import { AGE_GROUP_RANGES, ageInYear, audienceLabel, type Agenda, type AgendaActivity, type Availability, type AvailabilityItem } from '@koderek/shared';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Tabs,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useState, type CSSProperties, type MouseEvent } from 'react';
import { IconLayoutCards, IconMapPin, IconTable, IconUser } from '../../components/icons';
import { t } from '../../texts/pl';
import { formatTime } from '../editions/format';
import { EnrollmentAction } from '../enrollment/EnrollmentAction';
import { withAvailability, type EnrollmentControls } from '../enrollment/useEnrollment';
import classes from './AgendaView.module.css';
import { AGE_RANGE_KEYS, allDayRooms, applyFilters, seats, tileLayout, type AgeFilter, type AgendaFilters, type FilterPerson } from './agendaModel';

/** 20 minut = 48 px – mieszczą się symbol i dwie linijki tytułu. */
const PX_PER_MINUTE = 2.4;
/** Kolumna sali: minimalna szerokość i szerokość, do której agenda poszerza się ponad kontener strony. */
const ROOM_MIN_WIDTH = 170;
const ROOM_COMFORT_WIDTH = 240;
const TIME_COLUMN_WIDTH = 56;
const AGE_BUTTONS: { value: AgeFilter; label: string }[] = [
  { value: null, label: t.agenda.filterAgeAny },
  ...AGE_RANGE_KEYS.map((key) => ({ value: key, label: t.agenda.ageRange(...AGE_GROUP_RANGES[key]) })),
  { value: 'adult', label: t.agenda.filterAdult },
];

type AgendaTab = 'enroll' | 'open';
type AgendaLayout = 'grid' | 'list';

/** Wybór „Tabela / Lista” zapamiętany na tym urządzeniu (tylko wygoda – bez magazynu działa domyślna tabela). */
const LAYOUT_KEY = 'koderek.agendaLayout';

function readLayout(): AgendaLayout {
  try {
    return window.localStorage.getItem(LAYOUT_KEY) === 'list' ? 'list' : 'grid';
  } catch {
    return 'grid';
  }
}

function saveLayout(layout: AgendaLayout): void {
  try {
    window.localStorage.setItem(LAYOUT_KEY, layout);
  } catch {
    // Prywatne okno albo zablokowany magazyn – wybór po prostu nie zostanie zapamiętany.
  }
}

interface Props {
  agenda: Agenda;
  /**
   * Wybrana osoba (Z-301): na starcie włączony filtr „Dla: {imię}” – tylko zajęcia, na które pasuje wiekiem (BR-32).
   * Zmiana osoby (nowy `key` agendy) ustawia go od nowa.
   */
  person?: { firstName: string; kind: 'child' | 'adult'; birthYear: number | null };
  onOpen: (activity: AgendaActivity) => void;
  /** US-507: liczba miejsc z odpytywania. */
  availability?: Availability;
  /** US-501…US-503: zapisy wybranej osoby. */
  controls?: EnrollmentControls | null;
}

/**
 * US-301: agenda w dwóch zakładkach – zajęcia z zapisami (siatka sal i godzin na komputerze, lista po godzinach na telefonie,
 * AC-1) oraz wykłady, stoiska i pokazy bez zapisów (duże kafle). Filtry (AC-3): wiek (przy wybranej osobie tylko „Pasujące
 * dla: {imię} ({wiek})” i „Wszystkie grupy wiekowe” z jednozdaniową podpowiedzią), rodzaj – tylko wśród wykładów i stoisk, w jednej linii z wiekiem – i wolne miejsca w zajęciach z zapisami.
 */
export function AgendaView({ agenda, person, onOpen, availability, controls = null }: Props) {
  const filterPerson: FilterPerson | undefined = person
    ? { name: person.firstName, kind: person.kind, birthYear: person.birthYear, editionYear: Number(agenda.edition.date.slice(0, 4)) }
    : undefined;
  const defaults: AgendaFilters = { age: person ? 'person' : null, typeId: null, freeOnly: false };
  const [filters, setFilters] = useState<AgendaFilters>(defaults);
  // Wiek osoby na rok edycji (BR-32) – w etykiecie i podpowiedzi, żeby było jasne, że to filtr wieku.
  const personAge = !filterPerson
    ? undefined
    : filterPerson.kind === 'adult'
      ? t.agenda.personAdult
      : filterPerson.birthYear !== null
        ? t.agenda.personAge(ageInYear(filterPerson.birthYear, filterPerson.editionYear))
        : undefined;
  // Przy wybranej osobie przedziały wieku i „Dorośli” są zbędne – wystarczy „Pasujące dla: {imię}” i „Wszystkie grupy wiekowe”.
  const ageButtons: { value: AgeFilter; label: string }[] = person
    ? [
        { value: 'person', label: t.agenda.filterPerson(person.firstName, personAge) },
        { value: null, label: t.agenda.filterAgeAnyForPerson },
      ]
    : AGE_BUTTONS;
  const withEnrollment = agenda.activities.filter((a) => a.enrollment);
  const openEntry = agenda.activities.filter((a) => !a.enrollment);
  const [tab, setTab] = useState<AgendaTab>(withEnrollment.length ? 'enroll' : 'open');
  const [layout, setLayout] = useState<AgendaLayout>(readLayout);
  const live = withAvailability(agenda.activities, availability);
  const visible = applyFilters(live, { ...filters, freeOnly: tab === 'enroll' && filters.freeOnly }, filterPerson);
  const tile = { colors: new Map(agenda.types.map((type) => [type.id, type.color])), onOpen, controls, availability };
  const tabSource = tab === 'enroll' ? withEnrollment : openEntry;
  // Filtr rodzaju tylko w zakładce wykładów, stoisk i pokazów – w zajęciach z zapisami go nie ma.
  const usedTypes = tab === 'open' ? agenda.types.filter((type) => tabSource.some((a) => a.typeId === type.id)) : [];
  // Podział i kolumny z całej zakładki (bez filtrów), żeby siatka nie skakała przy zmianie filtra.
  const stands = allDayRooms(tabSource, agenda.types);
  const tabVisible = visible
    .filter((a) => a.enrollment === (tab === 'enroll'))
    .sort((x, y) => x.startsAt.localeCompare(y.startsAt));
  const allDay = tabVisible.filter((a) => stands.has(a.roomId));
  const timed = tabVisible.filter((a) => !stands.has(a.roomId));
  const gridSource = tabSource.filter((a) => !stands.has(a.roomId));
  // Kolumny tylko dla sal z zajęciami po filtrach – przy filtrze wieku agenda się zwęża. Zakres godzin zostaje z całości.
  const gridRooms = agenda.rooms.filter((room) => timed.some((a) => a.roomId === room.id));
  // Szeroka agenda: co najmniej szerokość strony, najwyżej obszar treści obok panelu menu (z marginesem) – tyle, ile wygodnie
  // potrzebują sale. Bez odjęcia panelu (`--app-shell-navbar-offset` z AppShell) lewa krawędź chowała się pod menu.
  const wide = `max(100%, min(100vw - var(--app-shell-navbar-offset, 0px) - 48px, ${TIME_COLUMN_WIDTH + gridRooms.length * ROOM_COMFORT_WIDTH}px))`;
  // „Wyczyść filtry” tylko, gdy coś odbiega od ustawień startowych (filtr osoby jest domyślny).
  const filtered = filters.age !== defaults.age || !!filters.typeId || filters.freeOnly;

  const clearButton = filtered ? (
    <Button variant="subtle" size="xs" onClick={() => setFilters(defaults)}>
      {t.agenda.clearFilters}
    </Button>
  ) : null;

  if (!agenda.activities.length) return <Text c="dimmed">{t.agenda.empty}</Text>;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" gap="sm" wrap="nowrap">
        {withEnrollment.length && openEntry.length ? (
          <Tabs
            value={tab}
            style={{ flex: 1, minWidth: 0 }}
            onChange={(value) => {
              setTab(value === 'open' ? 'open' : 'enroll');
              setFilters((f) => ({ ...f, typeId: null }));
            }}
          >
            <Tabs.List>
              <Tabs.Tab value="enroll">{t.agenda.tabEnroll(withEnrollment.length)}</Tabs.Tab>
              <Tabs.Tab value="open">{t.agenda.tabOpen(openEntry.length)}</Tabs.Tab>
            </Tabs.List>
          </Tabs>
        ) : (
          <span />
        )}
        {/* Telefon zawsze pokazuje listę – przełącznik tylko na szerszym ekranie. */}
        <SegmentedControl
          visibleFrom="md"
          color="brand"
          radius="md"
          aria-label={t.agenda.layout}
          value={layout}
          onChange={(value) => {
            const next = value === 'list' ? 'list' : 'grid';
            setLayout(next);
            saveLayout(next);
          }}
          data={[
            {
              value: 'grid',
              label: (
                <Group gap={6} wrap="nowrap" justify="center">
                  <IconTable size={16} />
                  <span>{t.agenda.layoutGrid}</span>
                </Group>
              ),
            },
            {
              value: 'list',
              label: (
                <Group gap={6} wrap="nowrap" justify="center">
                  <IconLayoutCards size={16} />
                  <span>{t.agenda.layoutList}</span>
                </Group>
              ),
            },
          ]}
        />
      </Group>

      <Stack gap="xs">
        <Group gap="sm" align="center" wrap="wrap">
          <Group gap={6} wrap="wrap" role="group" aria-label={t.agenda.filterAge}>
            <Text size="sm" fw={600} aria-hidden>
              {t.agenda.filterAgeLabel}
            </Text>
            {ageButtons.map((option) => {
              const active = filters.age === option.value;
              const forPerson = option.value === 'person';
              return (
                <Button
                  key={option.value ?? 'all'}
                  size="xs"
                  color={forPerson ? 'orange' : undefined}
                  variant={active ? 'filled' : forPerson ? 'light' : 'default'}
                  leftSection={forPerson ? <IconUser size={14} /> : undefined}
                  aria-pressed={active}
                  onClick={() => setFilters((f) => ({ ...f, age: option.value }))}
                >
                  {option.label}
                </Button>
              );
            })}
          </Group>
          {usedTypes.length > 1 ? (
            <Select
              aria-label={t.agenda.filterType}
              size="xs"
              data={[{ value: '', label: t.agenda.filterTypeAll }, ...usedTypes.map((type) => ({ value: type.id, label: type.name }))]}
              value={filters.typeId ?? ''}
              onChange={(value) => setFilters((f) => ({ ...f, typeId: value || null }))}
              allowDeselect={false}
              w={220}
            />
          ) : null}
          {tab === 'open' ? clearButton : null}
        </Group>
        {person ? (
          <Text size="xs" c="dimmed">
            {filters.age === 'person' ? t.agenda.personHintOn(person.firstName, personAge) : t.agenda.personHintOff(person.firstName)}
          </Text>
        ) : null}
        {tab === 'enroll' ? (
          <Group gap="sm" align="center" wrap="wrap">
            <Checkbox
              size="xs"
              label={t.agenda.filterFree}
              checked={filters.freeOnly}
              onChange={(event) => {
                const freeOnly = event.currentTarget.checked;
                setFilters((f) => ({ ...f, freeOnly }));
              }}
            />
            {clearButton}
          </Group>
        ) : null}
      </Stack>

      {/* Obie zakładki: tabela sal i godzin albo lista dużych kafli (pasek „Przez cały dzień” nad nimi). */}
      <>
          {!tabVisible.length ? <Text c="dimmed">{t.agenda.none}</Text> : null}
          {layout === 'grid' ? (
            <Stack gap="md" style={{ width: wide, marginLeft: `calc((100% - ${wide}) / 2)` }}>
              {allDay.length ? (
                <>
                  <Box visibleFrom="md">
                    <AllDayStrip agenda={agenda} activities={allDay} tile={tile} />
                  </Box>
                  <Box hiddenFrom="md">
                    <AllDayStrip agenda={agenda} activities={allDay} tile={tile} cards />
                  </Box>
                </>
              ) : null}
              <Box visibleFrom="md">
                {/* Przy pasku „Przez cały dzień” – nagłówek w tym samym stylu, żeby było jasne, gdzie zaczyna się plan godzinowy. */}
                {allDay.length && timed.length ? (
                  <Title order={3} size="h5" mb="xs">
                    {t.agenda.byHour}
                  </Title>
                ) : null}
                <AgendaGrid source={gridSource} rooms={gridRooms} activities={timed} tile={tile} />
              </Box>
              <Box hiddenFrom="md">
                <AgendaList agenda={agenda} activities={timed} tile={tile} />
              </Box>
            </Stack>
          ) : (
            <Stack gap="md">
              {allDay.length ? <AllDayStrip agenda={agenda} activities={allDay} tile={tile} cards /> : null}
              <AgendaList agenda={agenda} activities={timed} tile={tile} />
            </Stack>
          )}
      </>
    </Stack>
  );
}

/** BR-31: liczba miejsc na dużym kaflu – zielona, pomarańczowa w rezerwie, czerwona bez miejsc. */
function seatsColor(a: AgendaActivity): string {
  const { free } = seats(a);
  if (free === 0) return 'red';
  return a.enrolled >= a.capacity ? 'orange' : 'green';
}

/**
 * Duży kafel zajęć: godzina, sala w plakietce, tytuł, wiek, szczegóły. Wykład, stoisko, pokaz (BR-22) – z rodzajem w rogu.
 * Zajęcia z zapisami (widok listy, `tile`) – w rogu liczba miejsc, na dole „Zapisz”/„Wypisz” albo powód dla wybranej osoby.
 */
function ActivityCard({ activity: a, agenda, tile }: { activity: AgendaActivity; agenda: Agenda; tile: TileContext }) {
  const type = agenda.types.find((ty) => ty.id === a.typeId);
  const room = agenda.rooms.find((r) => r.id === a.roomId)?.name;
  const cancelled = a.status === 'cancelled';
  const onOpen = tile.onOpen;
  const withEnrollment = a.enrollment && !cancelled;
  const item = tile.controls?.items.get(a.id);
  const enrolled = item?.state === 'enrolled';
  const collision = item?.state === 'unavailable' && (item.reason === 'collision' || item.reason === 'travel_buffer');
  const live = tile.availability?.items.find((i) => i.activityId === a.id);
  return (
    <Paper
      withBorder
      radius="md"
      className={[classes.card, collision ? classes.collision : null].filter(Boolean).join(' ')}
      data-collision={collision || undefined}
      style={{
        opacity: cancelled ? 0.7 : 1,
        ...(enrolled ? { borderColor: 'var(--mantine-color-teal-5)', background: 'var(--mantine-color-teal-0)' } : {}),
        ...(collision ? { background: 'repeating-linear-gradient(135deg, var(--mantine-color-gray-1) 0 6px, var(--mantine-color-gray-2) 6px 12px)' } : {}),
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="xs">
        <Group gap="xs" wrap="wrap" miw={0}>
          <Text size="sm" fw={700} c="brand.7" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatTime(a.startsAt)}–{formatTime(a.endsAt)}
          </Text>
          {room ? (
            <Badge color="dark" variant="outline" tt="none" leftSection={<IconMapPin size={12} />}>
              {room}
            </Badge>
          ) : null}
        </Group>
        {cancelled ? (
          <Badge color="red" variant="light" tt="none" style={{ flex: 'none' }}>
            {t.agenda.cancelled}
          </Badge>
        ) : withEnrollment ? (
          <Badge color={seatsColor(a)} variant="light" tt="none" style={{ flex: 'none' }}>
            {seats(a).label}
          </Badge>
        ) : type ? (
          <Badge color={type.color} variant="light" tt="none" style={{ flex: 'none' }}>
            {type.name}
          </Badge>
        ) : null}
      </Group>
      <UnstyledButton onClick={() => onOpen(a)} aria-label={`${a.symbol} ${a.title}`} mt="sm" display="block">
        <Title order={3} size="h4" td={cancelled ? 'line-through' : undefined} lineClamp={2}>
          {a.title}
        </Title>
      </UnstyledButton>
      <Text size="sm" c="dimmed" mt={4}>
        {a.enrollment && type ? `${type.name} · ${audienceLabel(a)}` : audienceLabel(a)}
      </Text>
      {a.speakers ? (
        <Text size="sm" c="dimmed" lineClamp={1}>
          {a.speakers}
        </Text>
      ) : null}
      <Group gap="xs" mt="md" align="center">
        <Button variant="default" size="xs" onClick={() => onOpen(a)}>
          {t.agenda.details}
        </Button>
        {withEnrollment ? <EnrollmentAction activity={a} controls={tile.controls} live={live} opensAt={tile.availability?.opensAt} /> : null}
      </Group>
    </Paper>
  );
}

interface TileContext {
  colors: Map<string, string>;
  onOpen: (activity: AgendaActivity) => void;
  controls: EnrollmentControls | null;
  availability: Availability | undefined;
}

interface ListProps {
  agenda: Agenda;
  activities: AgendaActivity[];
  tile: TileContext;
}

/**
 * Stoiska, pokazy i gry na kilka godzin – karty nad siatką zamiast osobnych wąskich kolumn. W widoku listy (`cards`)
 * duże kafle jak reszta listy.
 */
function AllDayStrip({ agenda, activities, tile, cards = false }: ListProps & { cards?: boolean }) {
  const rooms = new Map(agenda.rooms.map((r) => [r.id, r.name]));
  return (
    <Stack gap="xs">
      <Title order={3} size="h5">
        {t.agenda.allDay}
      </Title>
      <div className={cards ? classes.cards : classes.allDay}>
        {activities.map((a) =>
          cards ? (
            <ActivityCard key={a.id} activity={a} agenda={agenda} tile={tile} />
          ) : (
            <ActivityTile key={a.id} activity={a} tile={tile} room={rooms.get(a.roomId)} />
          ),
        )}
      </div>
    </Stack>
  );
}

interface GridProps {
  /** Wszystkie zajęcia siatki (bez filtrów) – wyznaczają zakres godzin. */
  source: AgendaActivity[];
  rooms: Agenda['rooms'];
  activities: AgendaActivity[];
  tile: TileContext;
}

/**
 * Siatka: kolumny to sale w ustalonej kolejności, wysokość kafelka odpowiada czasowi trwania. Przewijana we własnym
 * oknie, z przyklejonym nagłówkiem sal i kolumną godzin.
 */
function AgendaGrid({ source, rooms, activities, tile }: GridProps) {
  if (!activities.length || !source.length) return null;
  const minutes = (iso: string) => new Date(iso).getTime() / 60_000;
  const dayStart = Math.floor(Math.min(...source.map((a) => minutes(a.startsAt))) / 60) * 60;
  const dayEnd = Math.ceil(Math.max(...source.map((a) => minutes(a.endsAt))) / 60) * 60;
  const height = (dayEnd - dayStart) * PX_PER_MINUTE;
  const hours = Array.from({ length: (dayEnd - dayStart) / 60 + 1 }, (_, i) => new Date((dayStart + i * 60) * 60_000).toISOString());

  return (
    <div className={classes.scroller}>
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: `${TIME_COLUMN_WIDTH}px repeat(${rooms.length}, minmax(${ROOM_MIN_WIDTH}px, 1fr))`,
          minWidth: TIME_COLUMN_WIDTH + rooms.length * ROOM_MIN_WIDTH,
        }}
      >
        <div className={classes.corner} />
        {rooms.map((room) => (
          <Text key={room.id} fw={600} size="sm" ta="center" p={4} className={classes.roomHeader}>
            {room.name}
            {room.hidden ? ` (${t.agenda.hiddenRoom})` : ''}
          </Text>
        ))}
        <Box className={classes.timeColumn} style={{ height }}>
          {hours.map((hour, i) => (
            <Text key={hour} size="xs" c="dimmed" style={{ position: 'absolute', top: Math.max(2, i * 60 * PX_PER_MINUTE - 7), right: 6 }}>
              {formatTime(hour)}
            </Text>
          ))}
        </Box>
        {rooms.map((room) => (
          <Box
            key={room.id}
            style={{
              position: 'relative',
              height,
              borderLeft: '1px solid var(--mantine-color-default-border)',
              backgroundImage: `repeating-linear-gradient(to bottom, var(--mantine-color-default-border) 0 1px, transparent 1px ${60 * PX_PER_MINUTE}px)`,
            }}
          >
            {activities
              .filter((a) => a.roomId === room.id)
              .map((a) => {
                const tileHeight = Math.max(28, (minutes(a.endsAt) - minutes(a.startsAt)) * PX_PER_MINUTE - 2);
                return (
                  <ActivityTile
                    key={a.id}
                    activity={a}
                    tile={tile}
                    gridHeight={tileHeight}
                    style={{
                      position: 'absolute',
                      left: 3,
                      right: 3,
                      top: (minutes(a.startsAt) - dayStart) * PX_PER_MINUTE + 1,
                      height: tileHeight,
                      overflow: 'hidden',
                    }}
                  />
                );
              })}
          </Box>
        ))}
      </Box>
    </div>
  );
}

/**
 * Widok listy (i telefon): rosnąco po godzinie, pogrupowane po godzinach rozpoczęcia – duże kafle jak w zakładce
 * „Zajęcia bez zapisów (otwarte wykłady, stoiska i pokazy)”, obok siebie na szerokim ekranie.
 */
function AgendaList({ agenda, activities, tile }: ListProps) {
  const groups = new Map<string, AgendaActivity[]>();
  for (const a of activities) {
    const key = formatTime(a.startsAt);
    groups.set(key, [...(groups.get(key) ?? []), a]);
  }
  return (
    <Stack gap="md">
      {[...groups.entries()].map(([time, items]) => (
        <Stack key={time} gap="xs">
          <Title order={3} size="h5">
            {time}
          </Title>
          <div className={classes.cards}>
            {items.map((a) => (
              <ActivityCard key={a.id} activity={a} agenda={agenda} tile={tile} />
            ))}
          </div>
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * US-301 AC-2, AC-4: kafelek zajęć; US-501: stan i akcja dla wybranej osoby. W siatce (`gridHeight`) treść układa się
 * według wysokości kafelka, a całość widać po najechaniu lub fokusie.
 */
function ActivityTile({
  activity: a,
  tile,
  room,
  gridHeight,
  style,
}: {
  activity: AgendaActivity;
  tile: TileContext;
  room?: string;
  gridHeight?: number;
  style?: CSSProperties;
}) {
  const cancelled = a.status === 'cancelled';
  const live: AvailabilityItem | undefined = tile.availability?.items.find((i) => i.activityId === a.id);
  const item = tile.controls?.items.get(a.id);
  const enrolled = item?.state === 'enrolled';
  // Wybrana osoba ma w tym czasie (albo tuż obok, w innym miejscu) inne zajęcia (US-502, powód z serwera) – kafelek wyszarzony,
  // powód w akcji na dole.
  const collision = item?.state === 'unavailable' && (item.reason === 'collision' || item.reason === 'travel_buffer');
  const compact = gridHeight !== undefined;
  const layout = compact ? tileLayout(gridHeight) : null;
  const titleFirst = layout?.titleFirst ?? false;
  const time = (
    <Text size="xs" c="dimmed">
      {formatTime(a.startsAt)}–{formatTime(a.endsAt)}
      {titleFirst ? '' : ` · ${a.symbol}`}
      {room ? ` · ${room}` : ''}
    </Text>
  );
  return (
    <Paper
      withBorder
      px={6}
      py={compact ? 4 : 6}
      radius="sm"
      className={[compact ? classes.gridTile : null, collision ? classes.collision : null].filter(Boolean).join(' ') || undefined}
      data-collision={collision || undefined}
      // Klik w dowolne miejsce kafelka (także pod „Zapisz”) otwiera szczegóły; przyciski i linki obsługują się same, a klików
      // z okien w portalu (np. pytanie przed wypisem) nie ma w drzewie DOM kafelka.
      onClick={(e: MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        if (e.currentTarget.contains(target) && !target.closest('button, a, input, select, textarea, [role="button"]')) tile.onOpen(a);
      }}
      style={{
        ...style,
        cursor: 'pointer',
        ...(compact ? { '--tile-height': `${gridHeight}px` } : {}),
        borderLeft: `4px solid ${collision ? 'var(--mantine-color-gray-4)' : (tile.colors.get(a.typeId) ?? 'var(--mantine-color-gray-5)')}`,
        opacity: cancelled ? 0.6 : 1,
        // Nieprzezroczyste tło – rozwinięty kafelek zasłania sąsiednie.
        background: enrolled
          ? 'linear-gradient(var(--mantine-color-teal-light), var(--mantine-color-teal-light)), var(--mantine-color-body)'
          : collision
            ? 'repeating-linear-gradient(135deg, var(--mantine-color-gray-1) 0 6px, var(--mantine-color-gray-2) 6px 12px)'
            : 'var(--mantine-color-body)',
      }}
    >
      <UnstyledButton onClick={() => tile.onOpen(a)} aria-label={`${a.symbol} ${a.title}`} w="100%">
        {titleFirst ? null : time}
        <Text size="sm" fw={600} lh={1.25} td={cancelled ? 'line-through' : undefined} lineClamp={layout?.titleLines}>
          {titleFirst ? (
            <Text span size="xs" fw={400} c="dimmed">
              {a.symbol} ·{' '}
            </Text>
          ) : null}
          {a.title}
        </Text>
        {titleFirst ? time : null}
        <Group gap={4} mt={2}>
          {cancelled ? (
            <Badge size="xs" color="red">
              {t.agenda.cancelled}
            </Badge>
          ) : (
            <Text size="xs">{seats(a).label}</Text>
          )}
          <Text size="xs" c="dimmed">
            · {audienceLabel(a)}
          </Text>
        </Group>
        {a.speakers && !compact ? (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {a.speakers}
          </Text>
        ) : null}
      </UnstyledButton>
      <EnrollmentAction activity={a} controls={tile.controls} live={live} opensAt={tile.availability?.opensAt} compact />
    </Paper>
  );
}
